import React from 'react';
import classnames from 'classnames';
import Styled from 'styled';
import { Query } from '@apollo/client/react/components';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import ClickAwayDetectableView from 'lib/ui/click-away-detectable-view';
import { keyEnterPressed, keyEscapePressed, keyDownPressed, keyUpPressed } from 'lib/key-code/key-code';

import Avatar from 'four-nets/avatar/avatar';
import deleteIcon from 'four-nets/icons/svg/icon_delete 2.svg';

import { Brand } from './brand-input';
import query, { BrandAutocompleteQuery, BrandAutocompleteQueryVariables } from './brand-autocomplete.graphql';
import defaultStyles from './brand-autocomplete.scss';

export interface Props {
    styles: typeof defaultStyles;
    value: string;
    searchValue: string;
    onChange: (brand: Brand) => void;
    children: (fn: (event: React.KeyboardEvent<HTMLInputElement>) => void, loading?: boolean) => React.ReactNode;
}

interface State {
    selectedIndex?: number;
    forceHide: boolean;
}

type Edges = NonNullable<NonNullable<NonNullable<BrandAutocompleteQuery['brands']>['edges']>>;

@ClickAwayDetectableView()
class BrandAutocomplete extends React.PureComponent<Props, State> {
    state: State = { forceHide: false };

    onClickAway = (event: Event) => {
        this.setState({ forceHide: true });
    };

    componentDidUpdate(prevProps: Props) {
        if (prevProps.searchValue !== this.props.searchValue && this.state.forceHide) {
            this.setState({ forceHide: false });
        }
    }

    focus = (node: Brand) => {
        this.setState({ forceHide: true }, () => this.props.onChange({ name: node.name, id: node.id }));
    };

    onKeyDown = (edges: Edges) => (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (keyDownPressed(event)) {
            this.setState(prevState => ({
                selectedIndex: Math.min(
                    edges.length - 1,
                    prevState.selectedIndex === undefined ? 0 : prevState.selectedIndex + 1
                ),
            }));
            event.stopPropagation();
        } else if (keyUpPressed(event)) {
            this.setState(prevState => ({
                selectedIndex: Math.max(0, (prevState.selectedIndex || 0) - 1),
            }));
            event.stopPropagation();
        } else if (keyEnterPressed(event)) {
            const brand = (edges[this.state.selectedIndex || 0] && edges[this.state.selectedIndex || 0]!.node) || null;
            this.focus(brand || { name: this.props.value, id: null });
            this.setState({ selectedIndex: undefined });
        } else if (keyEscapePressed(event)) {
            this.setState({ forceHide: true });
        }
    };

    render() {
        const { styles, searchValue } = this.props;
        const { forceHide } = this.state;

        return (
            <Query<BrandAutocompleteQuery, BrandAutocompleteQueryVariables>
                query={query}
                variables={{ name: searchValue }}
                notifyOnNetworkStatusChange={true}
                skip={!searchValue || searchValue.length < 3}
            >
                {({ data, loading }) => {
                    return (
                        <React.Fragment>
                            {this.props.children &&
                                this.props.children(
                                    this.onKeyDown((data && data.brands && data.brands.edges) || []),
                                    loading
                                )}
                            {searchValue && !forceHide && !loading && data && data.brands && data.brands.edges ? (
                                <div className={styles.wrapper}>
                                    <div className={styles.contentWrapper}>
                                        {data.brands.edges.map((edge, index) =>
                                            edge && edge.node ? (
                                                <button
                                                    key={edge.node.id}
                                                    type='button'
                                                    onClick={() => {
                                                        this.focus(edge.node!);
                                                    }}
                                                    className={classnames(styles.user, {
                                                        [styles.focused]: index === this.state.selectedIndex,
                                                    })}
                                                >
                                                    <Avatar
                                                        style={{ avatar: styles.avatar }}
                                                        user={edge.node.vendor && edge.node.vendor.user}
                                                    />
                                                    <div className={styles.info}>
                                                        <div className={styles.username}>{edge.node.name}</div>
                                                        <div className={styles.county}>
                                                            {edge.node.vendor &&
                                                                edge.node.vendor.county &&
                                                                edge.node.vendor.county.name}
                                                        </div>
                                                    </div>
                                                </button>
                                            ) : null
                                        )}
                                    </div>
                                    {searchValue &&
                                    !forceHide &&
                                    !loading &&
                                    data &&
                                    data.brands &&
                                    data.brands.edges &&
                                    data.brands.edges.length ? (
                                        <button
                                            className={styles.forceHide}
                                            onClick={() => this.setState({ forceHide: true })}
                                        >
                                            <SVGIcon style={{ image: styles.icon }} src={deleteIcon} />
                                        </button>
                                    ) : null}
                                </div>
                            ) : null}
                        </React.Fragment>
                    );
                }}
            </Query>
        );
    }
}

export default Styled(defaultStyles)(BrandAutocomplete);
